"use client";

import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { useTriggerAction, type BlokAction } from "@/lib/action-link";
import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname } from "@/lib/region";
import { override, pickSourceContent, type SourcedBlok } from "@/lib/sourced-blok";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type CtaCalloutFields = {
  heading?: string;
  accent_heading?: string;
  description?: string;
  features?: string;
  button_label?: string;
  button_link?: StoryblokLink;
  button_action?: BlokAction | "";
  bg_color?: string;
};

type CtaCalloutBlok = SbBlokData & SourcedBlok<CtaCalloutFields>;

function CheckCircle() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="shrink-0"
    >
      <circle cx="10" cy="10" r="9.1" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M6 10.4l2.6 2.6 5.4-5.6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function CtaCallout({ blok }: { blok: CtaCalloutBlok }) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const src = pickSourceContent(blok);
  const heading = override(blok.heading, src?.heading);
  const accentHeading = override(blok.accent_heading, src?.accent_heading);
  const description = override(blok.description, src?.description);
  const buttonLabel = override(blok.button_label, src?.button_label);
  const buttonLink = override(blok.button_link, src?.button_link);
  const buttonAction = override(blok.button_action, src?.button_action) || null;
  const bgColor = override(blok.bg_color, src?.bg_color);
  const featuresRaw = override(blok.features, src?.features);

  const { bg } = resolveBg(bgColor, "dusk-01");
  const link = resolveLink(buttonLink, region);
  const triggerAction = useTriggerAction();
  const showButton = Boolean(
    buttonLabel && (buttonAction || link.href !== "#"),
  );
  const features = (featuresRaw || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  const buttonClass =
    "inline-flex h-12 w-full items-center justify-center rounded-lg bg-white px-5 text-body font-bold text-purple-05 transition-colors hover:bg-purple-01 [@media(min-width:760px)]:w-[clamp(15rem,25vw,17.0625rem)] [@media(min-width:760px)]:shrink-0 [@media(min-width:760px)]:px-7 lg:h-14";

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(2.5rem,4vw,3rem)]">
        <div className="flex flex-col gap-[clamp(2rem,3vw,2.5rem)] rounded-[24px] bg-purple-05 px-[clamp(1.5rem,3.5vw,2.5rem)] py-[clamp(2.25rem,3.5vw,2.5rem)] text-white [@media(min-width:760px)]:flex-row [@media(min-width:760px)]:items-center [@media(min-width:760px)]:gap-10 lg:gap-[132px]">
          <div className="flex flex-col gap-[clamp(1.5rem,3vw,1.5625rem)] [@media(min-width:760px)]:flex-1 lg:w-[651px] lg:flex-initial lg:shrink-0">
            {(heading || accentHeading) && (
              <h2 className="text-[clamp(2.25rem,4vw,3rem)] font-bold leading-[1.08]">
                {heading && <span className="block">{heading}</span>}
                {accentHeading && (
                  <span className="block text-purple-01">{accentHeading}</span>
                )}
              </h2>
            )}
            {description && (
              <p className="text-body leading-[1.56] text-white/85 lg:text-h6 lg:leading-[1.52]">
                {description}
              </p>
            )}
            {features.length > 0 && (
              <ul className="flex flex-col gap-4 text-body [@media(min-width:560px)]:flex-row [@media(min-width:560px)]:flex-wrap [@media(min-width:560px)]:items-center [@media(min-width:560px)]:gap-x-6 lg:gap-x-4 lg:h-12 lg:py-4">
                {features.map((feat, i) => (
                  <li
                    key={`${i}-${feat}`}
                    className="flex items-center gap-2 leading-none"
                  >
                    <CheckCircle />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {showButton &&
            (buttonAction ? (
              <button
                type="button"
                onClick={(e) => triggerAction(buttonAction, e)}
                className={`cursor-pointer ${buttonClass}`}
              >
                {buttonLabel}
              </button>
            ) : (
              <a
                href={link.href}
                {...(link.target ? { target: link.target } : {})}
                className={buttonClass}
              >
                {buttonLabel}
              </a>
            ))}
        </div>
      </div>
    </section>
  );
}
